/**
 * pdf-export.ts — สร้าง PDF ของรายงานจากภาพ JPEG ที่ render ในเบราว์เซอร์ (T103)
 *
 * ทำไมใช้ภาพ: ข้อความไทยต้องใช้ฟอนต์ไทย ซึ่งการฝังฟอนต์ลง PDF ต้องแนบไฟล์ฟอนต์
 * (ดู docs/decisions/0004-pdf-export.md) — Phase 1 จึงวาดรายงานด้วยฟอนต์ของเครื่อง
 * ผู้ใช้แล้วฝังเป็นภาพ ทำให้ตัวอักษรไทยถูกต้อง 100% และตัวเลขตรงกับ payload เดียวกัน
 *
 * ฟังก์ชันที่ประกอบ PDF เป็น pure (เทสต์ได้) — ส่วนที่ต้องใช้ canvas อยู่ใน jpegFromSvg()
 */
import { readSvgSize } from './png-export.js';

/** ขนาดหน้า A4 ที่ 72dpi (จุด) */
export const A4 = { width: 595.28, height: 841.89 };
const MARGIN = 36;

const enc = new TextEncoder();

function concat(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

export interface PdfImagePage {
  jpeg: Uint8Array;
  /** ขนาดภาพต้นฉบับเป็น pixel */
  width: number;
  height: number;
}

/** คำนวณกรอบวางภาพให้พอดีหน้า A4 โดยรักษาสัดส่วน */
export function fitToPage(
  imgWidth: number,
  imgHeight: number,
): { width: number; height: number; x: number; y: number } {
  const maxW = A4.width - MARGIN * 2;
  const maxH = A4.height - MARGIN * 2 - 18; // เว้นที่เลขหน้า
  const scale = Math.min(maxW / imgWidth, maxH / imgHeight);
  const width = imgWidth * scale;
  const height = imgHeight * scale;
  return {
    width,
    height,
    x: (A4.width - width) / 2,
    y: A4.height - MARGIN - height,
  };
}

/**
 * ประกอบไฟล์ PDF (หนึ่งหน้าต่อหนึ่งภาพ) พร้อมเลขหน้า
 * โครงสร้าง: catalog → pages → page(s) → content stream + image XObject + Helvetica
 */
export function buildPdf(pages: PdfImagePage[]): Uint8Array {
  if (pages.length === 0) throw new Error('ไม่มีหน้าให้สร้าง PDF');
  const objects: Uint8Array[] = [];
  const addObject = (body: Uint8Array | string): number => {
    objects.push(typeof body === 'string' ? enc.encode(body) : body);
    return objects.length; // object number (1-based)
  };

  // จอง object 1 = catalog, 2 = pages (เขียนทีหลังเพราะต้องรู้เลข kid)
  objects.push(new Uint8Array(0));
  objects.push(new Uint8Array(0));
  const fontNo = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');

  const pageNumbers: number[] = [];
  pages.forEach((page, index) => {
    const box = fitToPage(page.width, page.height);
    const imageNo = addObject(
      concat([
        enc.encode(
          `<< /Type /XObject /Subtype /Image /Width ${page.width} /Height ${page.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.jpeg.length} >>\nstream\n`,
        ),
        page.jpeg,
        enc.encode('\nendstream'),
      ]),
    );
    const label = `${index + 1} / ${pages.length}`;
    const content =
      `q ${box.width.toFixed(2)} 0 0 ${box.height.toFixed(2)} ${box.x.toFixed(2)} ${box.y.toFixed(2)} cm /Im0 Do Q\n` +
      `BT /F1 9 Tf ${(A4.width / 2 - 12).toFixed(2)} ${MARGIN / 2 + 4} Td (${label}) Tj ET`;
    const contentNo = addObject(
      `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    );
    const pageNo = addObject(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${A4.width.toFixed(2)} ${A4.height.toFixed(2)}] ` +
        `/Resources << /XObject << /Im0 ${imageNo} 0 R >> /Font << /F1 ${fontNo} 0 R >> >> /Contents ${contentNo} 0 R >>`,
    );
    pageNumbers.push(pageNo);
  });

  objects[0] = enc.encode('<< /Type /Catalog /Pages 2 0 R >>');
  objects[1] = enc.encode(
    `<< /Type /Pages /Kids [${pageNumbers.map((n) => `${n} 0 R`).join(' ')}] /Count ${pageNumbers.length} >>`,
  );

  // เขียนไฟล์ + เก็บ byte offset ของแต่ละ object สำหรับ xref
  const header = enc.encode('%PDF-1.4\n%âãÏÓ\n');
  const chunks: Uint8Array[] = [header];
  let offset = header.length;
  const offsets: number[] = [];
  objects.forEach((body, i) => {
    offsets.push(offset);
    const prefix = enc.encode(`${i + 1} 0 obj\n`);
    const suffix = enc.encode('\nendobj\n');
    chunks.push(prefix, body, suffix);
    offset += prefix.length + body.length + suffix.length;
  });

  const xrefStart = offset;
  let xref = `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const o of offsets) xref += `${String(o).padStart(10, '0')} 00000 n \n`;
  xref += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF\n`;
  chunks.push(enc.encode(xref));
  return concat(chunks);
}

/** render SVG → JPEG (ใช้ canvas ของเบราว์เซอร์ เพื่อให้ฟอนต์ไทยถูกต้อง) */
export async function jpegFromSvg(
  svg: string,
  scale = 2,
): Promise<PdfImagePage> {
  const { width, height } = readSvgSize(svg);
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('แปลงภาพรายงานไม่สำเร็จ'));
      img.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(width * scale);
    canvas.height = Math.ceil(height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('เบราว์เซอร์นี้ไม่รองรับการสร้างภาพ');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('สร้างไฟล์ภาพไม่สำเร็จ'))),
        'image/jpeg',
        0.92,
      );
    });
    return {
      jpeg: new Uint8Array(await blob.arrayBuffer()),
      width: canvas.width,
      height: canvas.height,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** สร้าง PDF ของรายงานจาก SVG (หนึ่งหน้า) */
export async function pdfFromSvg(svg: string): Promise<Blob> {
  const page = await jpegFromSvg(svg);
  const bytes = buildPdf([page]);
  // copy ลง ArrayBuffer ของตัวเอง เพื่อให้ตรงกับ BlobPart (typed array อาจอยู่บน SharedArrayBuffer)
  const buffer = new ArrayBuffer(bytes.length);
  new Uint8Array(buffer).set(bytes);
  return new Blob([buffer], { type: 'application/pdf' });
}
